import { queryOptions } from "@tanstack/react-query";

import {
	apiPaths,
	createIntakeUploadResponseSchema,
	documentFilingAnswerSchema,
	intakeDocumentSchema,
	intakeDownloadResponseSchema,
	intakeFileDownloadPath,
	intakeFileFilingPath,
	intakeFilePath,
	intakeFilePreviewPath,
	intakeFilesPageSchema,
	intakePreviewResponseSchema,
	type IntakeDocumentStatus,
	type IntakeFile,
	type IntakeFolder,
} from "@ravnar/api-contract";

import { openDownload } from "../lib/download";
import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// Intake's requests. Every query key starts with "intake", so an upload can refresh the list.

/** How often the list is refreshed while an upload is still on its way through the copier. */
export const PENDING_REFETCH_INTERVAL_MS = 2000;

/**
 * The waits between asks while a document is still on its way to being read, widening as the wait goes on: the
 * copier files an upload within seconds, Bedrock reads it in a minute or two, and the Directory agent follows. Each
 * step says "while the youngest watched document is younger than `within`, ask every `every`".
 */
export const DOCUMENT_REFETCH_STEPS_MS = [
	{ within: 30_000, every: 2000 },
	{ within: 120_000, every: 5000 },
] as const;

/** What the page settles on once the youngest watched document is past the last step above. */
export const DOCUMENT_REFETCH_SLOW_MS = 15_000;

/**
 * How long after it was uploaded a document that has not reached Processed is still watched. It is a window rather
 * than a status test because a document Bedrock cannot read stays where it is for good — a job it refuses leaves the
 * file at `processing` with nothing to follow — and the page has no way to tell that from a document the copier filed
 * a second ago.
 */
export const DOCUMENT_WATCH_WINDOW_MS = 600_000;

/**
 * How long to wait before asking for this page of documents again. Anything not yet Processed and uploaded inside the
 * watch window is being watched; the youngest of them sets the wait, and when there is none the page stops asking.
 */
export function documentRefetchInterval(files: readonly IntakeFile[], now: number): number | false {
	let youngest: number | undefined;
	for (const file of files) {
		if (file.status === "processed") {
			continue;
		}
		const age = now - Date.parse(file.uploadedAt);
		if (age >= DOCUMENT_WATCH_WINDOW_MS) {
			continue;
		}
		if (youngest === undefined || age < youngest) {
			youngest = age;
		}
	}
	if (youngest === undefined) {
		return false;
	}
	const age = youngest;
	return DOCUMENT_REFETCH_STEPS_MS.find(step => age < step.within)?.every ?? DOCUMENT_REFETCH_SLOW_MS;
}

export interface IntakeFilesRequest {
	readonly status?: IntakeDocumentStatus;
	readonly mine?: boolean;
	/** The folder of the document type catalogue whose documents to list, and below it; every document when absent. */
	readonly folder?: IntakeFolder;
	/** What the search box holds; every document when it is empty. */
	readonly q?: string;
	readonly page?: string;
}

/**
 * A page of the organization's documents, newest first, in a folder and below it, with the folders inside it counted.
 * The API answers a page link it did not make, or made under other filters, with 400, and the page redirects to the
 * newest page on it.
 */
export const intakeFilesQueryOptions = ({ status, mine, folder, q = "", page }: IntakeFilesRequest = {}) =>
	queryOptions({
		queryKey: ["intake", "files", { status, mine: mine === true, folder, q: q.trim(), page }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.intakeFiles, intakeFilesPageSchema, {
				query: {
					...(status !== undefined && { status }),
					...(mine === true && { mine: "true" }),
					...(folder !== undefined && { folder }),
					...(q.trim() !== "" && { q: q.trim() }),
					...(page !== undefined && { page }),
				},
				signal,
			}),
		// The sidebar and the page both ask; a list a few seconds old is fine, and an upload refreshes it anyway.
		staleTime: LIST_STALE_TIME_MS,
		// A document is read after it is filed: the page keeps asking while one of its own is still on the way through,
		// and stops once every one of them is Processed or too old to still be moving.
		refetchInterval: query => documentRefetchInterval(query.state.data?.files ?? [], Date.now()),
		// A refused page link will not change on a retry, and the page redirects on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * How long after it was asked for an upload can still be on its way: its URL lasts fifteen minutes, a 100 MB PUT begun
 * at the last of them over a slow line takes about as long again, and the copier files it within seconds of its
 * landing. A file still pending after that was never sent — a browser's PUT failed and the file was tried again as
 * another, or software asked for a URL and never used it — and nothing will ever file it.
 */
export const UPLOAD_WATCH_WINDOW_MS = 30 * 60 * 1000;

/** The pending files that can still be on their way at `now`: those asked for inside the upload watch window. */
export function uploadsOnTheirWay(files: readonly IntakeFile[], now: number): IntakeFile[] {
	return files.filter(file => now - Date.parse(file.uploadedAt) < UPLOAD_WATCH_WINDOW_MS);
}

/**
 * What is still on its way: the uploads the copier has not filed yet, asked for inside the upload watch window
 * (`uploadsOnTheirWay`). The rail badge counts them, and the Intake page asks again while there are any; a file pending
 * for longer was never sent, and counting it would keep the badge up and every open page asking every two seconds for
 * good. It is its own request because the list is a page of documents now, and a pending file is not a document. It
 * counts at most `INTAKE_PAGE_SIZE`, the newest, which is more uploads than anyone has in flight.
 */
export const pendingIntakeFilesQueryOptions = () =>
	queryOptions({
		queryKey: ["intake", "files", { status: "pending" }],
		queryFn: async ({ signal }): Promise<IntakeFile[]> => {
			const { files } = await apiRequest(apiPaths.intakeFiles, intakeFilesPageSchema, {
				query: { status: "pending" },
				signal,
			});
			return uploadsOnTheirWay(files, Date.now());
		},
		staleTime: LIST_STALE_TIME_MS,
		refetchInterval: query => ((query.state.data?.length ?? 0) > 0 ? PENDING_REFETCH_INTERVAL_MS : false),
	});

/**
 * One document, for its own page. It is asked again on the list's schedule while it is still being read
 * (`documentRefetchInterval`), so its status moves on without a reload. A document Intake does not have answers 404,
 * which the page turns into the router's not-found page, and a refusal is not retried.
 */
export const intakeDocumentQueryOptions = (fileId: string) =>
	queryOptions({
		queryKey: ["intake", "document", fileId],
		queryFn: ({ signal }) => apiRequest(intakeFilePath(fileId), intakeDocumentSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
		refetchInterval: query => {
			const document = query.state.data;
			return document === undefined ? false : documentRefetchInterval([document], Date.now());
		},
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * How long after it was uploaded a document's filing is still asked for while Document Filing has not read it: half an
 * hour, three times the document's own watch, since Document Filing is the last step of the document's run — after
 * Bedrock's read, the Directory agent and the Document linker, each agent waiting its organization's turn.
 */
export const FILING_WATCH_WINDOW_MS = 30 * 60 * 1000;

/**
 * How long to wait before asking again what Document Filing read from a document it has not read yet: on the steps a
 * document is watched on, while it is young enough that Document Filing may still be at it
 * (`FILING_WATCH_WINDOW_MS`), and never after.
 */
export function filingRefetchInterval(uploadedAt: string, now: number): number | false {
	const age = now - Date.parse(uploadedAt);
	if (age >= FILING_WATCH_WINDOW_MS) {
		return false;
	}
	return DOCUMENT_REFETCH_STEPS_MS.find(step => age < step.within)?.every ?? DOCUMENT_REFETCH_SLOW_MS;
}

/**
 * What Document Filing read from one document — what it is, and what it says about money — for its Details. It is asked
 * again while it is unread and the document, uploaded at `uploadedAt`, is young enough to be read yet
 * (`filingRefetchInterval`).
 */
export const documentFilingQueryOptions = (fileId: string, uploadedAt: string) =>
	queryOptions({
		queryKey: ["intake", "filing", fileId],
		queryFn: ({ signal }) => apiRequest(intakeFileFilingPath(fileId), documentFilingAnswerSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
		refetchInterval: query =>
			query.state.data?.status === "unread" ? filingRefetchInterval(uploadedAt, Date.now()) : false,
	});

/**
 * Where to show one document from, which the API records in the audit log as a view when it hands it out. It is asked
 * once when the page opens and never again while it stays open: `"static"` keeps it from going stale and keeps every
 * refresh of `["intake"]` — an upload's, or a window coming back into focus — from asking again, which would reload the
 * frame, lose the reader's place and record a second view. It is forgotten the moment the page closes (`gcTime: 0`),
 * since the URL is only good for minutes. Nothing preloads it, so hovering a link to the page records nothing, and its
 * request is not cancellable: under React's StrictMode a cancelled first ask would be sent again.
 */
export const intakePreviewQueryOptions = (fileId: string) =>
	queryOptions({
		queryKey: ["intake", "preview", fileId],
		queryFn: () => apiRequest(intakeFilePreviewPath(fileId), intakePreviewResponseSchema),
		staleTime: "static",
		gcTime: 0,
		retry: false,
	});

/** The bucket refused the bytes: the presigned URL had expired, or the file was not the size that was announced. */
export class UploadError extends Error {
	readonly status: number;

	constructor(fileName: string, status: number) {
		super(`Uploading ${fileName} failed with status ${status}`);
		this.name = "UploadError";
		this.status = status;
	}
}

/**
 * Uploads one file: asks the API where to put it, which saves the file as pending, then PUTs the bytes there with
 * nothing but the file as the body. The URL is absolute and names the upload bucket in AWS; the local server answers a
 * path on the app's own origin, which resolves against the page. Answers the file as the API saved it; the copier
 * marks it stored a moment later. Fails with ApiError when the API turns the file down (400: its name or size; 409:
 * the organization's storage has no room for it) and with UploadError when the bucket does.
 */
export async function uploadIntakeFile(file: File): Promise<IntakeFile> {
	const { file: saved, upload } = await apiRequest(apiPaths.intakeUploads, createIntakeUploadResponseSchema, {
		method: "POST",
		body: { fileName: file.name, size: file.size },
	});
	// The bytes are read first and sent as they are. A browser would send a File body the same way, but the tests'
	// jsdom File is not one Node's fetch can read, and a buffer also makes the Content-Length the URL was signed for
	// certain. At most 100 MB, which the drop zone has already checked.
	const bytes = await file.arrayBuffer();
	const response = await fetch(upload.url, { method: upload.method, body: bytes });
	if (!response.ok) {
		throw new UploadError(file.name, response.status);
	}
	return saved;
}

/**
 * Asks the API where to get a document and follows the URL it answers. The API records the download in the audit log.
 * Fails with ApiError when the document is gone (404) or has not finished uploading (409).
 */
export async function downloadIntakeFile(fileId: string): Promise<void> {
	const { url } = await apiRequest(intakeFileDownloadPath(fileId), intakeDownloadResponseSchema);
	openDownload(url);
}
